import type { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import React, { useMemo } from 'react'
import { CustomManufacturerFields } from './CustomManufacturerFields'

export interface ExistingCustomManufacturer {
    name: string
    id: string
}

interface CustomManufacturerFieldsWrapperProps {
    selectedProjectElementNode?: Partial<ProjectElementNode>
    isHidden?: boolean
    /** Bug 2690689: id of the owning MFR/BOD paragraph for this input-field row.
     * Scopes the visible custom-mfr pills to that paragraph only — without it,
     * every per-PT row at family level reads from the global slice and shows
     * other PTs' values. */
    parentContentItemId: string
}

export const CustomManufacturerFieldsWrapper: React.FC<CustomManufacturerFieldsWrapperProps> = ({
    selectedProjectElementNode,
    isHidden = false,
    parentContentItemId,
}) => {
    const elementId = selectedProjectElementNode?.elementId ?? ''
    const projectId = selectedProjectElementNode?.projectId ?? ''

    const { parentFamilyId } = useElementItemShallowSelector((s) => ({
        parentFamilyId: s.elementItemValue.parentFamilyId,
    }))

    const { contentItemViewNodes } = useShallowSelector((state) => ({
        contentItemViewNodes: state.contentItemViewNodes,
    }))

    // For PRODUCTTYPE: use parentFamilyId (the family's elementId)
    // For PRODUCTFAMILY: use elementId (the family itself)
    const productFamilyId =
        selectedProjectElementNode?.elementType === 'PRODUCTTYPE'
            ? parentFamilyId ?? ''
            : selectedProjectElementNode?.elementId ?? ''

    // Custom-mfr pills owned by the MFR/BOD paragraph this input-field row
    // belongs to (Bug 2690689). Dedup is order-dependent — the first pill
    // for a given pillId in contentItemViewNodes wins.
    const existingCustomManufacturers = useMemo<
        ExistingCustomManufacturer[]
    >(() => {
        const existingData = new Set<string>()

        return (
            contentItemViewNodes
                ?.filter(
                    (node) =>
                        node.isCustomManufacturer &&
                        !node.isAddedInOfficeMasterDraftProject &&
                        node.dtvContentItemParentId === parentContentItemId
                )
                .map((node) => ({
                    name: node.elements?.[0]?.text ?? '',
                    id: node.pillId ?? '',
                }))
                .filter((item) => {
                    if (!item.id) return false
                    if (existingData.has(item.id)) return false
                    existingData.add(item.id)
                    return true
                }) ?? []
        )
    }, [contentItemViewNodes, parentContentItemId])

    if (!elementId || !productFamilyId || !projectId) {
        return null
    }

    return (
        <CustomManufacturerFields
            elementId={elementId}
            productFamilyId={productFamilyId}
            projectId={projectId}
            existingCustomManufacturers={existingCustomManufacturers}
            isHidden={isHidden}
        />
    )
}
